import {
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Text,
} from '@chakra-ui/react';

import { useQueryClient } from '@tanstack/react-query';

import { CloseCircleOutline, CountryCode, useToast } from '@aslanpay/portal-common-lib';

import useUpdatePartner from 'app/hooks/useUpdatePartner';

import EditPartnerForm, { EditPartnerFormFields } from '../EditPartnerForm';

import { EditPartnerModalProps } from './types';

function EditPartnerModal({ partner, isOpen, onClose }: EditPartnerModalProps) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { mutate: updatePartner, isLoading } = useUpdatePartner({
    onSuccess: () => {
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Partner updated',
        description: 'The partner was successfully updated.',
      });
      queryClient.invalidateQueries({ queryKey: ['fetchPartnersPage'] });
      onClose();
    },
    onError: () => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'Sorry, the partner could not be updated. Please try again.',
      });
    },
  });

  const handleSubmit = (data: EditPartnerFormFields) => {
    if (!partner) return;

    const {
      addressLine1,
      addressLine2,
      addressLine3,
      city,
      postCode,
      addressCountryCode,
      countryCode,
      ...rest
    } = data;

    const hasAddress =
      addressLine1 || addressLine2 || addressLine3 || city || postCode || addressCountryCode;

    updatePartner({
      id: partner.id,
      ...rest,
      countryCode: (countryCode as CountryCode) || undefined,
      address: hasAddress
        ? {
            addressLine1: addressLine1 || undefined,
            addressLine2: addressLine2 || undefined,
            addressLine3: addressLine3 || undefined,
            city: city || undefined,
            postCode: postCode || undefined,
            countryCode: (addressCountryCode as CountryCode) || undefined,
          }
        : undefined,
    });
  };

  if (!partner) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      closeOnOverlayClick={false}
      closeOnEsc={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <Text as="h6">Edit partner</Text>
            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircleOutline boxSize="30px" />}
              aria-label="Close edit partner"
              onClick={onClose}
            />
          </HStack>
        </ModalHeader>
        <ModalBody pb="24px">
          <EditPartnerForm
            id="edit-partner-form"
            partner={partner}
            onSubmit={handleSubmit}
            isLoading={isLoading}
            onClose={onClose}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default EditPartnerModal;
